import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

import React, { useState, useEffect } from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep, isObject } from "lodash";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Component
import SearchInput from "components/SearchInput";
import { SolidButton } from "components/buttons/SolidButton";
import Loading from "./loading";
import PortProfileModal from './modal';
import TableField from "components/Table";
import DrawerField from "components/Drawer";

// Util
import { checkEnvironment, checkMockup, apiMappingKey, deviceCategoryOption } from "./util/const";
import { getTableColumns, parseVlanRangeString } from "./util/func";
import Api from "./util/api";

const ProfilesPort = ({ showEditGeneral }) => {
  const theme = useTheme();
  const { scopeReady } = useOutletContext();
  const location = useLocation();
  const level = location.pathname.split("/")[1];
  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const columnHelper = createColumnHelper()

  //  State
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [auth, setAuth] = useState({
    scopeLevel: null,
    realm: null,
    orgs: null,
    sites: null,
    permissions: null,
  });
  const [uuidList, setUuidList] = useState({
    "read": uuidv4(),
  });
  const [originProfiles, setOriginProfiles] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [currentDrawerData, setCurrentDrawerData] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const columnsData = getTableColumns(
    columnHelper, profiles
  );
  const table = useTableInstance({
    tableName: "profilesPortTable",
    data: profiles,
    columns: columnsData,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  // Method
  const fetchTableData = async () => {
    setLoading(true);

    const api = Api.init(auth);
    const { apiKey, params } = api.getRequestParams(Api.actionTypeEnum.read);

    sendRequest(
      apiKey,
      uuidList.read,
      params,
    );
  };

  const handleSearchChange = (string) => {
    setSearchVal(string);

    if (string === "") {
      setProfiles(cloneDeep(originProfiles));
      return;
    }

    // Search name, vlan, state
    const filteredProfiles = originProfiles.filter((item) => {
      const name = item.profileName;
      const vlanList = parseVlanRangeString(item.allowVlan);
      const state = string.toLowerCase() === "enabled" ? "enabled" : string.toLowerCase() === "disabled" ? "disabled" : "";

      return (
        name.toLowerCase().includes(string.toLowerCase()) ||
        vlanList.some((vlan) => vlan.toString().includes(string)) ||
        state.toLowerCase().includes(string.toLowerCase())
      );
    });

    setProfiles(filteredProfiles);
  }

  const createAction = () => {
    setOpenModal(true);
  }

  const handleCloseModal = () => {
    setOpenModal(false);
    setUuidList((prev) => ({
      ...prev,
      create: uuidv4(),
    }));
  }

  const handleSaveChanges = () => {
    handleCloseModal();
    fetchTableData();
  }

  const handleDrawerClose = () => {
    setCurrentDrawerData({});
    setDrawerOpen(false);
    setCurrentDrawerData(null);
  }

  const handleDrawerApplyCallback = () => {
    fetchTableData();
    setDrawerOpen(false);
  }

  // Side effect
  useEffect(() => {
    setAuth((prev) => ({
      ...prev,
      scopeLevel: level,
    }));
  }, []);

  useEffect(() => {
    if (scopeReady || checkMockup === "true") {
      const { realm, orgs, site } = JSON.parse(
        localStorage.getItem("selectBreadcrumbs")
      );
      if (isObject(site) && Object.keys(site).length > 0) {
        setAuth((prev) => ({
          ...prev,
          scopeLevel: "site",
          realm,
          orgs,
          sites: site,
          permissions: site.roleName,
        }));
      } else {
        const currentPermissions = checkEnvironment === "premises" ? realm.roleName : orgs.roleName;
        setAuth((prev) => ({
          ...prev,
          scopeLevel: "org",
          realm,
          orgs,
          permissions: currentPermissions,
        }));
      }
    }
  }, [scopeReady]);

  useEffect(() => {
    if (!auth.scopeLevel) return;
    fetchTableData();
  }, [auth.scopeLevel, auth.realm, auth.orgs, auth.sites]);

  useEffect(() => {
    // Read
    if(!dataMap?.[uuidList.read]) return;

    const readResponse = getData(uuidList.read);
    if (readResponse) {
      const data = (readResponse.data || []).map((profile) => {
        const item = {};
        const keys = Object.keys(apiMappingKey);

        for (let j = 0; j < keys.length; j++) {
          if (keys[j] === apiMappingKey.deviceCategory) {
            item[keys[j]] = deviceCategoryOption[profile[apiMappingKey.deviceCategory]]?.label || deviceCategoryOption?.unknown?.label;
            continue;
          }

          if (keys[j] === apiMappingKey.assignedPort) {
            item[keys[j]] = profile[apiMappingKey.assignedPort]?.join(", ") || '-';
            item['assignedPortLength'] = profile[apiMappingKey.assignedPort]?.length || 0;
            continue;
          }

          if (keys[j] === apiMappingKey.allowVlan) {
            const value = profile[apiMappingKey.allowVlan];
            item[keys[j]] = value ? value : '-';
            continue;
          }

          item[keys[j]] = profile[apiMappingKey[keys[j]]];
        }

        item.ref = cloneDeep(profile);
        return item;
      });

      setOriginProfiles(data);
      setProfiles(data);
      setLoading(false);
    }

  }, [dataMap?.[uuidList.read]]);


  return <>
    <Box sx={{ padding: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "80px",
          gap: 3,
        }}
      >
        <Divider
          sx={{ width: "1px", height: "40px", margin: "20px 0" }}
          orientation="vertical"
          flexItem
        />

        <SearchInput
          value={searchVal}
          placeholder="Search by Name, VLAN, State"
          onChange={handleSearchChange}
        />
        {
          showEditGeneral && <SolidButton
            startIcon={<AddIcon />}
            width="240px"
            height="32px"
            onClick={createAction}
          >
            Create New Port Profile
          </SolidButton>
        }
      </Box>

      <PortProfileModal
        auth={auth}
        openModal={openModal}
        handleCloseModal={handleCloseModal}
        onSaveChanges={handleSaveChanges}
      />

      <TableField
        {...table}
        pendingChildren={
          loading && <Loading />
        }
        drawerOpen={drawerOpen}
        handleRowOnClick={(row) => {
          setCurrentDrawerData(row);
          setDrawerOpen(true);
        }}
      />
    </Box>

    {
      drawerOpen &&
      <DrawerField
        contentKey={currentDrawerData?.id || `ProfilesPort_${uuidv4()}`}
        open={drawerOpen}
        width="700px"
        title="Port Profile Details"
        handleClose={handleDrawerClose}
        commonContent="settingsProfilePort"
        contentProps={{
          auth,
          selectedPort: currentDrawerData?.ref,
          onDrawerApplyCallback: handleDrawerApplyCallback,
          showEditGeneral,
        }}
      />
    }
  </>
};

export default ProfilesPort;
